import { useEffect, useRef } from 'react';
import {
  startSpanOnActiveTransaction,
  finishSpan,
} from '@leagueplatform/observability';

type UseLoginTimingMetricProps = {
  appInitIsSuccess: boolean;
  appInitIsFetching: boolean;
  appInitIsError: boolean;

  userProfileIsSuccess: boolean;
  userProfileIsFetching: boolean;
  userProfileIsError: boolean;
};

/**
 * Measures each phase of the login flow as separate Sentry performance spans.
 *
 * measurements.login-auth0
 * measurements.login-app-init
 * measurements.login-user-profile
 *
 * Span attributes:
 * - cached=true|false
 * - outcome=success|error|abandoned
 */
export const useLoginTimingMetric = ({
  appInitIsSuccess,
  appInitIsFetching,
  appInitIsError,
  userProfileIsSuccess,
  userProfileIsFetching,
  userProfileIsError,
}: UseLoginTimingMetricProps): void => {
  const auth0SpanSent = useRef(false);

  const appInitSpanRef =
    useRef<ReturnType<typeof startSpanOnActiveTransaction>>();
  const appInitSpanFinished = useRef(false);
  const appInitWasFetchingOnMount = useRef(false);

  const userProfileSpanRef =
    useRef<ReturnType<typeof startSpanOnActiveTransaction>>();
  const userProfileSpanFinished = useRef(false);
  const userProfileWasFetchingOnMount = useRef(false);

  useEffect(() => {
    // Reset so StrictMode's second mount starts with a clean slate.
    appInitSpanFinished.current = false;
    userProfileSpanFinished.current = false;

    if (!auth0SpanSent.current) {
      auth0SpanSent.current = true;

      const auth0Span = startSpanOnActiveTransaction({
        op: 'measurements.login-auth0',
        description: 'login - auth0 authentication',
        startTimestamp: performance.timeOrigin / 1000,
      });

      if (auth0Span) {
        finishSpan(auth0Span);
      }
    }

    appInitWasFetchingOnMount.current = appInitIsFetching;

    appInitSpanRef.current = startSpanOnActiveTransaction({
      op: 'measurements.login-app-init',
      description: 'login - app_init_details response time',
    });

    if (appInitIsSuccess && appInitSpanRef.current) {
      appInitSpanFinished.current = true;

      appInitSpanRef.current.setAttribute(
        'cached',
        !appInitWasFetchingOnMount.current,
      );
      appInitSpanRef.current.setAttribute('outcome', 'success');

      finishSpan(appInitSpanRef.current);
    }

    userProfileWasFetchingOnMount.current = userProfileIsFetching;

    userProfileSpanRef.current = startSpanOnActiveTransaction({
      op: 'measurements.login-user-profile',
      description: 'login - user_profile response time',
    });

    if (userProfileIsSuccess && userProfileSpanRef.current) {
      userProfileSpanFinished.current = true;

      userProfileSpanRef.current.setAttribute(
        'cached',
        !userProfileWasFetchingOnMount.current,
      );
      userProfileSpanRef.current.setAttribute('outcome', 'success');

      finishSpan(userProfileSpanRef.current);
    }

    return () => {
      if (appInitSpanRef.current && !appInitSpanFinished.current) {
        appInitSpanFinished.current = true;

        appInitSpanRef.current.setAttribute(
          'cached',
          !appInitWasFetchingOnMount.current,
        );

        appInitSpanRef.current.setAttribute('outcome', 'abandoned');

        finishSpan(appInitSpanRef.current);
      }

      if (userProfileSpanRef.current && !userProfileSpanFinished.current) {
        userProfileSpanFinished.current = true;

        userProfileSpanRef.current.setAttribute(
          'cached',
          !userProfileWasFetchingOnMount.current,
        );

        userProfileSpanRef.current.setAttribute('outcome', 'abandoned');

        finishSpan(userProfileSpanRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (appInitSpanFinished.current || !appInitSpanRef.current) {
      return;
    }

    if (appInitIsSuccess) {
      appInitSpanFinished.current = true;

      appInitSpanRef.current.setAttribute(
        'cached',
        !appInitWasFetchingOnMount.current,
      );

      appInitSpanRef.current.setAttribute('outcome', 'success');

      finishSpan(appInitSpanRef.current);

      return;
    }

    if (appInitIsError) {
      appInitSpanFinished.current = true;

      appInitSpanRef.current.setAttribute(
        'cached',
        !appInitWasFetchingOnMount.current,
      );

      appInitSpanRef.current.setAttribute('outcome', 'error');

      finishSpan(appInitSpanRef.current);
    }
  }, [appInitIsSuccess, appInitIsError]);

  useEffect(() => {
    if (userProfileSpanFinished.current || !userProfileSpanRef.current) {
      return;
    }

    if (userProfileIsSuccess) {
      userProfileSpanFinished.current = true;

      userProfileSpanRef.current.setAttribute(
        'cached',
        !userProfileWasFetchingOnMount.current,
      );

      userProfileSpanRef.current.setAttribute('outcome', 'success');

      finishSpan(userProfileSpanRef.current);

      return;
    }

    if (userProfileIsError) {
      userProfileSpanFinished.current = true;

      userProfileSpanRef.current.setAttribute(
        'cached',
        !userProfileWasFetchingOnMount.current,
      );

      userProfileSpanRef.current.setAttribute('outcome', 'error');

      finishSpan(userProfileSpanRef.current);
    }
  }, [userProfileIsSuccess, userProfileIsError]);
};
